import { useEffect, useRef, useState } from "react"
import { BaseTable, TableHeader, NumberInput, Modal, MultipleChart, MultipleChartData } from "components"
import { convertNumber, useToggle } from "utils"
import { useSaveFinanceBudget } from "../utils/UseSaveFinanceBudget"
import { KPIEntityRow } from "../types/FinanceKpiTypes"

import {
  ChartOptions
} from "chart.js"
import { TbChartBar } from "react-icons/tb"
import { persentage_category } from "../types/FinanceMonthlyType"

type Props = {
  list: KPIEntityRow[]
  isFilterReady: boolean
  year?: number
  setList: React.Dispatch<React.SetStateAction<KPIEntityRow[]>>
  locationId?: number | null
}

export default function FinanceKPIEntityTable({
  list,
  isFilterReady,
  year,
  setList,
  locationId
}: Props) {

  const { saveBudget } = useSaveFinanceBudget()

  // ambil semua lokasi (header)
  const locationHeaders =
    list.length > 0 ? list[0].locations.map((l) => l.name) : []

  // budget state
  const [budgets, setBudgets] = useState<Record<string, string>>({})

  useEffect(() => {
    const initial = Object.fromEntries(
      list.map((item) => [item.code_budget, item.budget ?? ""])
    )
    setBudgets(initial)
  }, [list])

  const debounceTimer = useRef<Record<string, any>>({})

  return (
    <div className="overflow-x-auto">
      <BaseTable className="table table-bordered w-full">
        <TableHeader>
          <tr className="bg-black text-white text-xs">
            <th className="text-left">KEY KPI</th>
            <th className="text-right">BUDGET</th>
            <th className="text-right">AVERAGE</th>

            {locationHeaders.map((loc) => (
              <th key={loc} className="text-right min-w-[120px]">
                {loc.toUpperCase()}
              </th>
            ))}

            <th className="text-center">ACTION</th>
          </tr>
        </TableHeader>

        <tbody>
          {list.map((item) => {

            
            return(
          
              <tr key={item.code_budget}>
                
                {/* KEY KPI */}
                <td className="font-medium !align-middle min-w-[200px]">
                  {item.key_description}
                </td>

                {/* BUDGET */}
                <td className="text-right">
                  <NumberInput
                    disabled={!isFilterReady}
                    className="min-w-[100px]"
                    textRight
                    suffix={persentage_category.includes(item.code_budget ?? "") ? " %" : ""}
                    value={budgets[item.code_budget] ?? ""}
                    onValueChange={(e, sourceInfo) => {
                      if (sourceInfo.source !== "event") return

                      const value = e.floatValue?.toString() ?? ""

                      setBudgets((prev) => ({
                        ...prev,
                        [item.code_budget]: value
                      }))

                      // debounce save
                      if (debounceTimer.current[item.code_budget]) {
                        clearTimeout(debounceTimer.current[item.code_budget])
                      }

                      debounceTimer.current[item.code_budget] = setTimeout(async () => {
                        if (!year || !value) return

                        const result = await saveBudget({
                          budget_id: item.budget_id,
                          code_budget: item.code_budget,
                          year,
                          budget: value,
                          ...(locationId != null
                            ? { location_id: locationId }
                            : {})
                        })

                        if (!result) return

                        const newBudgetId =
                              result.cr_budget_loc_id ??
                              result.cr_budget_global_id

                        setList(prev =>
                          prev.map(row =>
                            row.code_budget === item.code_budget
                              ? {
                                  ...row,
                                  budget_id: newBudgetId ?? row.budget_id,
                                  budget: value
                                }
                              : row
                          )
                        )

                      }, 2000)
                    }}
                  />
                </td>

                {/* AVERAGE */}
                <td className="text-right">
                  {item.average
                    ? persentage_category.includes(item.code_budget ?? "")
                      ? `${convertNumber(Number(item.average)).intoCurrency} %`
                      : convertNumber(Number(item.average)).intoCurrency
                    : ""}
                </td>

                {/* LOCATION VALUES */}
                {locationHeaders.map((header) => {
                  const loc = item.locations.find((l) => l.name === header)
                  const isPercentage = persentage_category.includes(item.code_budget ?? "")
                  return (
                    <td key={header} className="text-right">
                      {loc?.value != null && loc.value !== 0
                        ? isPercentage
                          ? `${convertNumber(loc.value).intoCurrency} %`
                          : convertNumber(loc.value).intoCurrency
                        : ""}
                    </td>
                  )
                })}

                {/* ACTION */}
                <td className="text-center">
                  <ChartSectionKPI
                      title={item.key_description}
                      locations={item.locations}
                      budget={Number(item.budget ?? 0)}
                      average={Number(item.average ?? 0)}
                      formula={item.formula ?? ""}
                      disabled={!isFilterReady}
                      isPercentage={persentage_category.includes(item.code_budget ?? "")}
                  />
              </td>
              </tr>
            )}
              
          )}
        </tbody>
      </BaseTable>
    </div>
  )
}

type PropsChart = {
    title: string
    locations: {
      name: string
      value: number
    }[]
    formula?: string | null
    budget?: number
    average?: number
    disabled?: boolean
    isPercentage?: boolean
  }
export function ChartSectionKPI({
    title,
    locations,
    budget = 0,
    average = 0,
    formula = "",
    disabled,
    isPercentage = false
}: PropsChart) {

    const { isActive, toggle } = useToggle(false)
    const [chartType, setChartType] = useState<"line" | "bar">("bar")

    const labels = locations.map((l) => l.name)
    const values = locations.map((l) => l.value)

    const data: MultipleChartData = {
        labels,
        datasets: [
            {
            label: "Actual",
            data: values,
            borderColor: "#3B82F6",
            backgroundColor: "rgba(59,130,246,0.2)",
            borderWidth: 2
            }
        ]
    }

    const options: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: 20
        },
      
        scales: {
          x: {
            position: chartType === "line" ? "bottom" : "center",
      
            ticks: {
              display: chartType === "line"
            },
      
            grid: { display: false },
            border: { display: false }
          },
      
          y: {
            grid: {
              color: (ctx) =>
                ctx.tick.value === 0
                  ? "rgb(0,0,0)"
                  : "rgba(0,0,0,0)"
            },
            border: {
              display: false
            }
          }
        },
      
        plugins: {
          legend: { display: false },
      
          datalabels:
            chartType === "bar"
              ? {
                  clip: false,
                  labels: {
                    location: {
                      color: "#666",
      
                      formatter: (_, context) => {
                        return labels[context.dataIndex]
                      },
      
                      anchor: (context) => {
                        const value =
                          context.dataset.data[context.dataIndex] as number
                        return value > 0 ? "start" : "end"
                      },
      
                      align: (context) => {
                        const value =
                          context.dataset.data[context.dataIndex] as number
                        return value > 0 ? "bottom" : "top"
                      }
                    },
      
                    value: {
                      display: (ctx) =>
                        ctx.dataset.data[ctx.dataIndex] !== 0,
                      color: "#000",
                      font: {
                        weight: "bold"
                      },
      
                      formatter: (value: number) => {
                        return isPercentage
                          ? `${convertNumber(value).intoCurrency} %`
                          : convertNumber(value).intoCurrency
                      },
      
                      anchor: (context) => {
                        const value =
                          context.dataset.data[context.dataIndex] as number
                        return value < 0 ? "start" : "end"
                      },
      
                      align: (context) => {
                        const value =
                          context.dataset.data[context.dataIndex] as number
                        return value < 0 ? "bottom" : "top"
                      }
                    }
                  }
                }
              : { display: false },
      
          annotation: {
            annotations: {
              budgetLine: {
                type: "line",
                yMin: budget,
                yMax: budget,
                borderColor: "#DC2626",
                borderWidth: 2,
                borderDash: [6, 6],
      
                label: {
                  display: true,
                  content: `BUDGET (${convertNumber(budget).intoCurrency}${isPercentage ? " %" : ""})`,
                  position: "end",
                  color: "#DC2626",
                  font: { weight: "bold" },
                  yAdjust: -10,
                  backgroundColor: "transparent"
                }
              },
      
              averageLine: {
                type: "line",
                yMin: average,
                yMax: average,
                borderColor: "#ff9d00",
                borderWidth: 2,
                borderDash: [6, 6],
      
                label: {
                  display: true,
                  content: `AVERAGE (${convertNumber(average).intoCurrency}${isPercentage ? " %" : ""})`,
                  position: "end",
                  color: "#ff9d00",
                  font: { weight: "bold" },
                  yAdjust: -10,
                  backgroundColor: "transparent"
                }
              }
            }
          }
        }
      }

    return (
    <>
        
        <button
        onClick={toggle}
        disabled={disabled}
        className="btn btn-xs btn-warning text-white"
        >
        <TbChartBar size={16} />
        </button>

        {isActive && (
        <Modal
            isOpen
            title={`${title.toUpperCase()} CHART`}
            closeModal={toggle}
            isdarktitle
        >
            <Modal.Body>
                <div className="flex justify-end mb-2">
                <select
                    className="select select-sm select-bordered"
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value as "line" | "bar")}
                >
                    {/* <option value="line">Line</option> */}
                    <option value="bar">Bar</option>
                </select>
                </div>

                <div style={{ height: 400 }}>
                <MultipleChart
                    className="min-h-[400px]"
                    chartType={chartType as "bar"}
                    data={data}
                    options={options}
                />
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
    
                {/* FORMULA */}
                <div className="border-2 border-gray-400 min-h-[300px]rounded-lg p-4">
                    <h2 className="font-bold mb-3">FORMULA</h2>

                    <div className="text-sm space-y-2">
                        <div
                            dangerouslySetInnerHTML={{ __html: formula ?? "" }}
                        />
                    </div>
                </div>

                {/* AI ANALYTIC */}
                <div className="border-2 border-gray-400 min-h-[300px] rounded-lg p-4">
                    <h2 className="font-bold mb-3">AI ANALYTIC</h2>

                    <div className="text-sm text-gray-600">
                    
                    </div>
                </div>

                </div>

            </Modal.Body>
        </Modal>
        )}
    </>
    )
}